import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { nanoid } from '@reduxjs/toolkit';
import CompanyLogo from 'app/components/CommonStyled/CompanyLogo';
import DataTypePill from 'app/components/CommonStyled/DataTypePill/DataTypePill';
import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import { PulseLeadIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { SkeletonItem } from 'app/components/DetailsPages/shared/styled';
import Tooltip from 'app/components/Tooltip';
import {
  ChatCircleIcon,
  FullHeartIcon,
  OpenInNewIcon,
  ResourceBestOf2024Icon,
  SearchIconSmall,
  TagIcon,
} from 'app/components/shared/icons';
import ResourceBestOf2026Icon from 'app/components/shared/icons/resource-best-of-2026';
import dayjs from 'dayjs';
import useBehavioral from 'hooks/useBehavioral';
import useCommentsCounts from 'hooks/useCommentsCounts';
import useIsLiked from 'hooks/useIsLiked';
import useMetaTags from 'hooks/useMetaTags';
import { resourceModalSelector } from 'store/reducers/global/global-reducer';
import { arrayToOxford, htmlToPlainString, singularOrPlural } from 'utils';

import ResourceClickTarget from '../ResourceClickTarget';
import { useResourceClick } from '../hooks/useResourceClick';
import { resolveResourceClick, ResourceFormat } from '../resourceClickBehavior';
import { bestOfAwardLabel as formatBestOfAwardLabel } from './bestOfAwardLabel';
import {
  ResourceCardContainer,
  ResourceCardDescription,
  ResourceCardExtraInformationWrapper,
  ResourceCardImage,
  ResourceCardImageContainer,
  ResourceCardImageLikesText,
  ResourceCardLikesContainer,
  ResourceCardPeopleContainer,
  ResourceCardTitle,
  ResourceCardWrapper,
  ResourceCardNLPMatchesRow,
  ResourceCardNLPMatchesLabel,
  ResourceCardNLPMatchesDetail,
  ResourceCardNLPMatchesItem,
  ResourceCardHeaderWrapper,
  ResourceCardHeaderRightContainer,
  ResourceCardHeaderTitle,
  ResourceCardHeaderLeftContainer,
  ResourceCardCommentsContainer,
  ResourceCardPeopleTagWrapper,
  ResourceCardPeopleTagText,
  ResourceCardImageTagContainer,
  ResourceCardClients,
  ResourceCardHeaderCompaniesContainer,
  ResourceCardHeaderDate,
  ResourceCardPeopleOverallContainer,
  ResourceCardNewTabIndicator,
} from './styled';

export { ResourceFormat } from '../resourceClickBehavior';

export interface IResourceCompany {
  name: string;
  slug: string;
}

export interface IResourcePerson {
  uuid: string;
  slug: string;
  name: string;
  avatar: string;
  role?: string;
  company?: IResourceCompany;
  is_alumni?: boolean;
  hide_alumni_label?: boolean;
}

export interface IResourceContributor {
  people: IResourcePerson[];
  companies: IResourceCompany[];
  communities: IResourceCommunity[];
}

export interface IResourceCommunity {
  slug: string;
  name: string;
}

export interface IResourceLikes {
  count: number;
  people: {
    samples: IResourcePerson[];
    uuids: string[];
  };
}

export interface IResourceSubmissionCategory {
  name: string;
  slug: string;
  icon?: string;
  icon_url?: string;
}

export interface IResourceFormat {
  name: string;
  singular: string;
  plural: string;
  icon: string;
  icon_background: string;
}

export interface IResource {
  uuid: string;
  slug: string;
  title: string;
  submitted_at?: string;
  started_at?: string;
  released_at?: string;
  downloadable?: boolean;
  has_paywall: boolean;
  short_description?: string;
  type?: IResourceFormat;
  resource_format_name?: string;
  long_description?: string;
  image?: string;
  image_variants?: {
    [key: string]: string;
  };
  link?: string;
  content_html?: string;
  link_text?: string;
  tags: string[];
  contributors: IResourceContributor;
  submitter: any;
  header_size?: 'small' | 'medium' | 'large';
  url?: string;
  related_resources?: IResource[];
  related_pages?: any[];
  open_in_modal?: boolean;
  likes?: IResourceLikes;
  liked_by_you?: boolean;
  voted?: boolean;
  available_to_vote?: boolean;
  status?: 'draft' | 'published';
  internal_share_status?: 'open' | 'blinded';
  external_client_share_status?: 'granted' | 'denied' | 'unknown';
  sharing_notes?: string | null;
  resource_submission_category?: IResourceSubmissionCategory;
  vote_count?: number;
  vote_percentage?: number;
  best_of_2024?: 'winner' | 'honoree' | null;
  comments_count?: number;
  clients?: {
    uuid: string;
    name: string;
    slug: string;
  }[];
  skills?: {
    uuid: string;
    name: string;
    slug: string;
  }[];
  project_categories?: {
    uuid: string;
    name: string;
    slug: string;
  }[];
  additional_images?: string[];
  impact_metrics?: string;
  pulse_submission?: boolean;
  voting_companies?: {
    company_name: string;
    company_slug: string;
    company_uuid: string;
  }[];
  best_of_year: {
    year: number;
    status: string;
    placement_rank?: number;
    category?: IResourceSubmissionCategory;
    category_award?: {
      status: 'winner' | 'honoree';
      placement_rank: number;
      category: IResourceSubmissionCategory;
    } | null;
    jury_prizes?: { uuid: string; name: string; icon: string | null }[];
  };
}

const RESOURCE_IMAGE_FALLBACK = '/images/resources/resource-fallback-image.png';

enum RESOURCE_CARD_HEADER_STRATEGY {
  COMPANIES = 'companies',
  PEOPLE = 'people',
  COMPANIES_AND_PEOPLE = 'companies and people',
  NONE = 'none',
}

interface ResourceCardProps {
  resource: IResource;
  behavioralOnClick?: () => void;
  overrideResourceClick?: (resource: IResource) => void;
  nlpMatches?: string[];
  preventLayoutGroup?: boolean;
  freezeHoverOnOpen?: boolean;
  showClients?: boolean;
  alwaysShowDescription?: boolean;
  uniformLayout?: boolean;
}

export default function ResourceCard({
  resource,
  behavioralOnClick,
  overrideResourceClick,
  nlpMatches,
  preventLayoutGroup = false,
  freezeHoverOnOpen = false,
  showClients = false,
  alwaysShowDescription = false,
  uniformLayout = false,
}: ResourceCardProps) {
  const uniqueId = useMemo<string>(() => {
    return nanoid();
  }, []);
  const { commentsCount, handleSetCommentsCount } = useCommentsCounts({
    type: 'resource',
    uuid: resource.uuid,
  });
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const coverImage = [resource.image_variants?.xl, resource.image].find(
    (url) => url && !failedImages.includes(url)
  );
  const metadataUrl = useMemo(() => {
    if (
      !resource.link ||
      [ResourceFormat.PDF, ResourceFormat.IMAGE, ResourceFormat.EMBEDDABLE_HTML].includes(
        resource.resource_format_name as ResourceFormat
      )
    ) {
      return undefined;
    }

    try {
      const url = new URL(resource.link, window.location.origin);
      // OS pages and media handlers are full resources, not thumbnail providers.
      if (
        !['http:', 'https:'].includes(url.protocol) ||
        url.origin === window.location.origin ||
        ['/file_resource', '/embeddable_resource'].includes(url.pathname)
      ) {
        return undefined;
      }
      return url.href;
    } catch {
      return undefined;
    }
  }, [resource.link, resource.resource_format_name]);
  const metadata = useMetaTags(metadataUrl, {
    enabled: !!metadataUrl && !coverImage,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 600000,
  });
  const resourceModal = useSelector(resourceModalSelector);
  const [hovering, setHovering] = useState<boolean>(false);
  const [frozenHovering, setFrozenHovering] = useState<boolean | null>(null);
  const [isOpeningFromClick, setIsOpeningFromClick] = useState<boolean>(false);
  const resourceDetailClickedFunction = useBehavioral('RESOURCE_DETAIL_CLICKED');
  const resourceOpenFunction = useBehavioral('RESOURCE_OPENED');
  const isLiked = useIsLiked(resource.slug, resource.likes?.people.uuids);
  const handleResourceClick = useResourceClick();
  const ref = useRef(null);
  const bestOfAward =
    resource.best_of_year ||
    (resource.best_of_2024
      ? {
          year: 2024,
          status: resource.best_of_2024,
          category: resource.resource_submission_category,
        }
      : null);

  useEffect(() => {
    handleSetCommentsCount(resource.comments_count);
  }, []);

  useEffect(() => {
    if (!freezeHoverOnOpen) return;

    if (resourceModal?.slug === resource?.slug) {
      setIsOpeningFromClick(false);
      return;
    }

    if (!isOpeningFromClick && frozenHovering !== null) {
      setFrozenHovering(null);
    }
  }, [freezeHoverOnOpen, frozenHovering, isOpeningFromClick, resource?.slug, resourceModal?.slug]);

  const companies = useMemo<IResourceCompany[]>(() => {
    return resource.contributors.companies;
  }, [resource.contributors.companies]);

  const people = useMemo<IResourcePerson[]>(() => {
    return resource.contributors.people;
  }, [resource.contributors.people]);

  const strategy = useMemo<RESOURCE_CARD_HEADER_STRATEGY>(() => {
    if (companies.length === 0 && people.length === 0) {
      return RESOURCE_CARD_HEADER_STRATEGY.NONE;
    }

    if (companies.length >= 1) {
      return RESOURCE_CARD_HEADER_STRATEGY.COMPANIES;
    }

    return RESOURCE_CARD_HEADER_STRATEGY.PEOPLE;
  }, [companies, people]);

  const clickDecision = useMemo(
    () =>
      resolveResourceClick({
        slug: resource.slug,
        open_in_modal: resource.open_in_modal,
        resource_format_name: resource.resource_format_name,
        link: resource.link,
      }),
    [resource.open_in_modal, resource.link, resource.resource_format_name, resource.slug]
  );

  const opensInNewTab = !overrideResourceClick && clickDecision.opensInNewTab;

  const handleResourceCardClick = (event?: React.MouseEvent<HTMLElement>) => {
    const originScrollPosition = window.scrollY;

    if (overrideResourceClick) {
      return overrideResourceClick(resource);
    }

    if (
      freezeHoverOnOpen &&
      clickDecision.type === 'modal' &&
      (!event || (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey))
    ) {
      setFrozenHovering(hovering);
      setIsOpeningFromClick(true);
    }

    const behavioralFunction = () => {
      const behavioral =
        clickDecision.type === 'modal' ? resourceDetailClickedFunction : resourceOpenFunction;

      return behavioral({ resource_id: resource.slug });
    };

    handleResourceClick(
      {
        open_in_modal: resource.open_in_modal,
        resource_format_name: resource.resource_format_name,
        slug: resource.slug,
        unique_id: uniqueId,
        link: resource.link,
        behavioralAction: behavioralOnClick || behavioralFunction,
        originScrollPosition,
      },
      event
    );
  };

  const handleResourceCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;

    event.preventDefault();
    handleResourceCardClick();
  };

  const thumbnail = metadataUrl && metadata.data?.img_url;
  const resourceImage =
    coverImage ||
    (thumbnail && !failedImages.includes(thumbnail) ? thumbnail : RESOURCE_IMAGE_FALLBACK);

  const handleImageError = () => {
    if (resourceImage === RESOURCE_IMAGE_FALLBACK) return;
    setFailedImages((previous) =>
      previous.includes(resourceImage) ? previous : [...previous, resourceImage]
    );
  };

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    setHovering(false);
  };

  const imageAnimationState = useMemo<'hover' | 'notHover'>(() => {
    if ((isOpeningFromClick || resourceModal?.slug === resource?.slug) && frozenHovering !== null) {
      return frozenHovering ? 'hover' : 'notHover';
    }

    return hovering ? 'hover' : 'notHover';
  }, [frozenHovering, hovering, isOpeningFromClick, resource?.slug, resourceModal?.slug]);

  return (
    <ResourceCardWrapper
      animate={{
        visibility: resourceModal?.slug !== resource?.slug ? 'visible' : 'hidden',
      }}
      {...(overrideResourceClick
        ? {
            onClick: handleResourceCardClick,
            onKeyDown: handleResourceCardKeyDown,
            role: 'button',
            tabIndex: 0,
            'aria-label': `View ${resource.title}`,
          }
        : {})}
      {...(!preventLayoutGroup
        ? {
            layoutId: `resource-card-${resource?.slug}-${uniqueId}`,
          }
        : {})}
      data-slug={resource?.slug}
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {overrideResourceClick ? null : (
        <ResourceClickTarget
          decision={clickDecision}
          label={resource.title}
          onClick={handleResourceCardClick}
        />
      )}
      <ResourceCardContainer data-id="resource-card-container">
        {opensInNewTab ? (
          <ResourceCardNewTabIndicator aria-label="Opens in a new tab" title="Opens in a new tab">
            <OpenInNewIcon width={14} height={14} fill="#000000" />
          </ResourceCardNewTabIndicator>
        ) : null}
        <ResourceCard.Header
          resource={resource}
          strategy={strategy}
          resourceImage={resourceImage}
        />
        <ResourceCardImageContainer
          variants={{
            hover: { height: 108 },
            notHover: { height: 144 },
          }}
          initial="notHover"
          animate={imageAnimationState}
        >
          {![
            RESOURCE_CARD_HEADER_STRATEGY.PEOPLE,
            RESOURCE_CARD_HEADER_STRATEGY.COMPANIES_AND_PEOPLE,
          ].includes(strategy) && people.length > 0 ? (
            <ResourceCardImageTagContainer>
              <ResourceCard.PeopleTag resource={resource} />
            </ResourceCardImageTagContainer>
          ) : null}
          <ResourceCardImage
            src={resourceImage}
            alt={resource.title}
            loading="lazy"
            onError={handleImageError}
          />
        </ResourceCardImageContainer>
        {showClients ? (
          <ResourceCardClients>
            {resource.clients?.map((client) => (
              <DataTypePill
                key={client.uuid}
                type="client"
                title={client.name}
                slug={client.slug}
                themeMode="light"
                clickable={false}
              />
            ))}
          </ResourceCardClients>
        ) : null}
        <ResourceCardTitle bestOf2024={Boolean(bestOfAward)}>
          {resource.title}{' '}
          {bestOfAward ? (
            <Tooltip text={formatBestOfAwardLabel(bestOfAward)}>
              <div style={{ display: 'inline-block', transform: 'translateY(3px)' }}>
                {bestOfAward.year === 2026 ? (
                  <ResourceBestOf2026Icon />
                ) : (
                  <ResourceBestOf2024Icon />
                )}
              </div>
            </Tooltip>
          ) : null}
        </ResourceCardTitle>
        <ResourceCardDescription
          variants={{
            hidden: { maxHeight: 0, opacity: 0 },
            visible: { maxHeight: 200, opacity: 1 },
          }}
          initial="hidden"
          animate={hovering || alwaysShowDescription || uniformLayout ? 'visible' : 'hidden'}
        >
          {htmlToPlainString(resource.short_description)}
        </ResourceCardDescription>
        {nlpMatches?.length > 0 && (
          <ResourceCardNLPMatchesRow>
            <ResourceCardNLPMatchesLabel>MATCHES:</ResourceCardNLPMatchesLabel>
            <ResourceCardNLPMatchesDetail>
              {nlpMatches.map((match, index) => (
                <ResourceCardNLPMatchesItem key={`nlp-match-${resource.uuid}-${index}`}>
                  <SearchIconSmall fill={'rgba(23, 28, 51, 0.5)'} width={15} height={15} />
                  <span>{match}</span>
                </ResourceCardNLPMatchesItem>
              ))}
            </ResourceCardNLPMatchesDetail>
          </ResourceCardNLPMatchesRow>
        )}
      </ResourceCardContainer>
      <ResourceCardExtraInformationWrapper data-id="resource-card-extra-information">
        <ResourceCardLikesContainer data-id="resource-card-likes">
          <FullHeartIcon
            width={18}
            height={18}
            fill={isLiked ? '#D47563' : 'none'}
            border={isLiked ? '#D47563' : 'var(--color-primary)'}
          />
          <ResourceCardImageLikesText>
            {resource.likes?.count === 0 && isLiked
              ? `1 Like`
              : resource.likes?.count === 0
              ? 'Like'
              : `${resource.likes?.count} ${singularOrPlural(
                  resource.likes?.count,
                  'Like',
                  'Likes'
                )}`}
          </ResourceCardImageLikesText>
        </ResourceCardLikesContainer>
        <ResourceCardCommentsContainer>
          <ChatCircleIcon width={18} height={18} fill={'var(--color-primary)'} fillOpacity={1} />
          {commentsCount !== 0 ? (
            <ResourceCardImageLikesText>
              {commentsCount} {singularOrPlural(commentsCount, 'Comment', 'Comments')}
            </ResourceCardImageLikesText>
          ) : (
            <ResourceCardImageLikesText>Comment</ResourceCardImageLikesText>
          )}
        </ResourceCardCommentsContainer>
      </ResourceCardExtraInformationWrapper>
    </ResourceCardWrapper>
  );
}

interface ResourceCardHeaderProps {
  resource: IResource;
  strategy: RESOURCE_CARD_HEADER_STRATEGY;
  resourceImage: string;
}

ResourceCard.Header = function ResourceCardHeader({
  resource,
  strategy,
  resourceImage,
}: ResourceCardHeaderProps) {
  const companies = useMemo<IResourceCompany[]>(() => {
    return resource.contributors.companies;
  }, [resource.contributors.companies]);

  const people = useMemo<IResourcePerson[]>(() => {
    return resource.contributors.people;
  }, [resource.contributors.people]);

  const title = useMemo<string>(() => {
    if (strategy === RESOURCE_CARD_HEADER_STRATEGY.COMPANIES) {
      return arrayToOxford(companies.map((company) => company.name));
    }

    if (strategy === RESOURCE_CARD_HEADER_STRATEGY.COMPANIES_AND_PEOPLE) {
      return `${companies[0].name} and ${people[0].name}`;
    }

    if (strategy === RESOURCE_CARD_HEADER_STRATEGY.PEOPLE) {
      if (people.length === 1) {
        return people[0].name;
      }

      if (people.length === 2) {
        return `${people[0].name} and ${people[1].name}`;
      }

      if (people.length === 3) {
        return `${arrayToOxford(people.slice(0, 3).map((person) => person.name))}`;
      }

      return `${arrayToOxford(people.slice(0, 3).map((person) => person.name))} and ${
        people.length - 3
      } other${people.length - 3 === 1 ? '' : 's'}`;
    }

    return '';
  }, [strategy, companies, people]);

  if (companies.length === 0 && people.length === 0) {
    return null;
  }

  return (
    <ResourceCardHeaderWrapper>
      {companies.length > 0 || people.length > 0 ? (
        <ResourceCardHeaderLeftContainer>
          {strategy === RESOURCE_CARD_HEADER_STRATEGY.COMPANIES ? (
            <>
              <ResourceCard.Companies companies={companies} />
              {resource.pulse_submission ? <PulseLeadIcon width={20} height={20} /> : null}
            </>
          ) : null}
          {strategy === RESOURCE_CARD_HEADER_STRATEGY.PEOPLE ? (
            <ResourceCardPeopleContainer hasMore={people.length > 3}>
              {people.slice(0, 3).map((person, index) => (
                <PersonAvatar
                  slug={person.slug}
                  key={`${person.slug}-${index}`}
                  avatar={person.avatar}
                  size={40}
                  name={person.name.split(' ')[0]}
                  lastName={person.name.substring(person.name.indexOf(' ') + 1)}
                  hideTooltip={false}
                  highlighted
                  openDrawer
                />
              ))}
              {resource.pulse_submission ? <PulseLeadIcon width={20} height={20} /> : null}
            </ResourceCardPeopleContainer>
          ) : null}
          {strategy === RESOURCE_CARD_HEADER_STRATEGY.COMPANIES_AND_PEOPLE ? (
            <ResourceCardPeopleOverallContainer>
              <ResourceCardPeopleContainer hasMore={false}>
                <PersonAvatar
                  highlighted
                  slug={resource.contributors.people[0].slug}
                  avatar={resource.contributors.people[0].avatar}
                  size={40}
                  name={resource.contributors.people[0].name.split(' ')[0]}
                  lastName={resource.contributors.people[0].name.split(' ')?.[1]}
                />
                <ResourceCard.Companies companies={resource.contributors.companies} />
              </ResourceCardPeopleContainer>
              {resource.pulse_submission ? <PulseLeadIcon width={20} height={20} /> : null}
            </ResourceCardPeopleOverallContainer>
          ) : null}
        </ResourceCardHeaderLeftContainer>
      ) : null}
      <ResourceCardHeaderRightContainer>
        <ResourceCardHeaderTitle>{title}</ResourceCardHeaderTitle>
        {resource.started_at ? (
          <ResourceCardHeaderDate>
            {dayjs(resource.started_at).format('MMM YYYY')}
            {resource.released_at && <>- {dayjs(resource.released_at).format('MMM YYYY')}</>}
          </ResourceCardHeaderDate>
        ) : null}
        {people.length > 0 &&
        ![
          RESOURCE_CARD_HEADER_STRATEGY.PEOPLE,
          RESOURCE_CARD_HEADER_STRATEGY.COMPANIES_AND_PEOPLE,
        ].includes(strategy) &&
        !resourceImage ? (
          <ResourceCard.PeopleTag resource={resource} />
        ) : null}
      </ResourceCardHeaderRightContainer>
    </ResourceCardHeaderWrapper>
  );
};

ResourceCard.PeopleTag = function ResourceCardPeopleTag({ resource }: ResourceCardProps) {
  const people = useMemo<IResourcePerson[]>(() => {
    return resource.contributors.people;
  }, [resource]);

  const text = useMemo<string>(() => {
    if (people.length === 1) {
      return people[0].name;
    }

    return `${people[0].name} and ${people.length - 1} other${people.length - 1 === 1 ? '' : 's'}.`;
  }, [people]);

  const tooltipText = useMemo<string>(() => {
    return arrayToOxford(people.map((person) => person.name));
  }, [people]);

  return (
    <Tooltip text={tooltipText}>
      <ResourceCardPeopleTagWrapper>
        <TagIcon />
        <ResourceCardPeopleTagText>{text}</ResourceCardPeopleTagText>
      </ResourceCardPeopleTagWrapper>
    </Tooltip>
  );
};

ResourceCard.Companies = function ResourceCardCompanies({
  companies,
}: {
  companies: IResourceCompany[];
}) {
  return (
    <ResourceCardHeaderCompaniesContainer>
      {companies.map((company, index) => (
        <CompanyLogo useRounded slug={company.slug} size={40} key={`${company.slug}-${index}`} />
      ))}
    </ResourceCardHeaderCompaniesContainer>
  );
};

ResourceCard.Skeleton = function ResourceCardSkeleton() {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 288,
        minWidth: 288,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 10,
        padding: 16,
        background: '#ffffff',
        borderRadius: 20,
        boxShadow: '0px 6px 24px 0px rgba(0, 0, 0, 0.1)',
      }}
    >
      <SkeletonItem animated style={{ width: 40, height: 40, borderRadius: '100%' }} />
      <SkeletonItem animated style={{ width: 142, height: 18 }} />
      <SkeletonItem animated style={{ width: '100%', height: 144, borderRadius: 8 }} />
      <SkeletonItem animated style={{ width: 'calc(100% - 20px)', height: 24 }} />
      <SkeletonItem animated style={{ width: '100%', height: 14 }} />
      <SkeletonItem animated style={{ width: '50%', height: 14 }} />
    </div>
  );
};
